import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faBriefcase } from "@fortawesome/pro-solid-svg-icons/faBriefcase";
import { faCalendarAlt } from "@fortawesome/pro-solid-svg-icons/faCalendarAlt";
import { faChartBar } from "@fortawesome/pro-solid-svg-icons/faChartBar";
import { faClipboardList } from "@fortawesome/pro-solid-svg-icons/faClipboardList";
import { faMap } from "@fortawesome/pro-solid-svg-icons/faMap";
import { faRoad } from "@fortawesome/pro-solid-svg-icons/faRoad";
import AntDMenu, { MenuProps } from "antd/es/menu";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";

import Path from "@app/enum/Path";
import { getReportsDefaultPath } from "@app/store/cumul";
import { isResourcesEnabled } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { navbarMenuClick } from "@app/util/analytic/navbarAnalytics";

import styles from "./CommonMenu.module.scss";
import MenuItemTitle from "./MenuItemTitle";

interface Props {
  isResourcesEnabled: boolean;
  reportsDefaultPath: string;
}

const Menu = ({ isResourcesEnabled, reportsDefaultPath }: Props) => {
  const intl = useIntl();

  const { pathname } = useLocation();
  const mapActive = pathname.startsWith(Path.MAP);
  const planActive = pathname.startsWith(Path.ACTIVITY);
  const listActive =
    pathname.startsWith(Path.COMPANY) ||
    pathname.startsWith(Path.PEOPLE) ||
    pathname.startsWith(Path.DEAL);
  const reportsActive = pathname.startsWith(Path.REPORTS);
  const resourcesActive = pathname.startsWith(Path.RESOURCES);
  const routesActive = pathname.startsWith(Path.ROUTE);

  const handleNavigate = useCallback<NonNullable<MenuProps["onSelect"]>>((info) => {
    navbarMenuClick(info!.key);
  }, []);

  return (
    <nav className={styles.container}>
      <AntDMenu
        className={styles.menu}
        mode="horizontal"
        onSelect={handleNavigate}
        triggerSubMenuAction="click"
      >
        <AntDMenu.Item className={styles.topItem} key="map">
          <MenuItemTitle active={mapActive} icon={faMap} itemName={Path.MAP} path={Path.MAP}>
            {intl.formatMessage({
              id: "navbar.menu.map",
              defaultMessage: "Map",
              description: "Title of the Map menu item",
            })}
          </MenuItemTitle>
        </AntDMenu.Item>
        <AntDMenu.Item className={styles.topItem} key="planner">
          <MenuItemTitle
            active={planActive}
            icon={faCalendarAlt}
            itemName={Path.ACTIVITY}
            path={Path.ACTIVITY}
          >
            {intl.formatMessage({
              id: "navbar.menu.planner",
              defaultMessage: "Planner",
              description: "Title of the Planner menu item",
            })}
          </MenuItemTitle>
        </AntDMenu.Item>
        {/* Routes Menu */}
        <AntDMenu.Item className={styles.topItem} key="routes">
          <MenuItemTitle
            active={routesActive}
            icon={faRoad}
            itemName={Path.ROUTE}
            path={Path.ROUTE}
          >
            {intl.formatMessage({
              id: "navbar.menu.routes",
              defaultMessage: "Routes",
              description: "Title of the Routes menu item",
            })}
          </MenuItemTitle>
        </AntDMenu.Item>
        {/* Records Menu */}
        <AntDMenu.SubMenu
          className={styles.topItem}
          key="records"
          popupClassName={styles.submenu}
          title={
            <MenuItemTitle active={listActive} hasSubmenu icon={faClipboardList} itemName="records">
              {intl.formatMessage({
                id: "navbar.menu.records",
                defaultMessage: "Records",
                description: "Title of the Records menu item",
              })}
            </MenuItemTitle>
          }
        >
          <AntDMenu.Item key="companiesListRecords">
            <MenuItemTitle itemName={Path.COMPANY} path={Path.COMPANY}>
              {intl.formatMessage({
                id: "navbar.menu.companies",
                defaultMessage: "Companies",
                description: "Title of the Companies List menu item",
              })}
            </MenuItemTitle>
          </AntDMenu.Item>
          <AntDMenu.Item key="peopleListRecords">
            <MenuItemTitle itemName={Path.PEOPLE} path={Path.PEOPLE}>
              {intl.formatMessage({
                id: "navbar.menu.people",
                defaultMessage: "People",
                description: "Title of the People List menu item",
              })}
            </MenuItemTitle>
          </AntDMenu.Item>
          <AntDMenu.Item key="funnelsDealsRecords">
            <MenuItemTitle itemName={Path.DEAL} path={Path.DEAL}>
              {intl.formatMessage({
                id: "navbar.menu.funnelsAndDeals",
                defaultMessage: "Deals and Funnels",
                description: "Title of the Funnels/Deals menu item",
              })}
            </MenuItemTitle>
          </AntDMenu.Item>
        </AntDMenu.SubMenu>

        {/* Reports Menu */}
        <AntDMenu.Item className={styles.topItem} key="reports">
          <MenuItemTitle
            active={reportsActive}
            icon={faChartBar}
            itemName={Path.REPORTS}
            path={reportsDefaultPath}
          >
            {intl.formatMessage({
              id: "navbar.menu.reports",
              defaultMessage: "Reports",
              description: "Title of the Reports menu item",
            })}
          </MenuItemTitle>
        </AntDMenu.Item>

        {isResourcesEnabled && (
          <AntDMenu.Item className={styles.topItem} key="resources">
            <MenuItemTitle
              active={resourcesActive}
              icon={faBriefcase}
              itemName={Path.RESOURCES}
              path={Path.RESOURCES}
            >
              {intl.formatMessage({
                id: "navbar.menu.resources",
                defaultMessage: "Resources",
                description: "Title of the Resources menu item",
              })}
            </MenuItemTitle>
          </AntDMenu.Item>
        )}
      </AntDMenu>
    </nav>
  );
};

const mapStateToProps = (state: RootState) => ({
  isResourcesEnabled: isResourcesEnabled(state),
  reportsDefaultPath: getReportsDefaultPath(state),
});

export default connect(mapStateToProps)(Menu);
